import React from 'react';
import { Badge, Button, Collapse, Descriptions, Tooltip } from 'antd';
import moment from 'moment';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { LicenseDetailNoteFormContainer as NoteFormContainer } from '../../containers';
import { LICENSE_TYPE_JWT } from '../../constants';
import { byteConversion, fileExport } from '../../utils';

import styles from './LicenseDetail.module.scss';

const { Panel } = Collapse;

const customPanelStyle = {
  background: '#f7f7f7',
  borderRadius: 4,
  border: 0,
  textAlign: 'left',
  overflow: 'hidden'
};

const LicenseDetail = ({
  id,
  status,
  isAdmin,
  detail,
  endTime,
  closeModal,
  type,
  notes,
  getContractFile
}) => {
  const {
    Use: use,
    Audience: audience,
    GraphStudio: gst,
    Insights: insights,
    GSQLShell: gsqlshell,
    GraphQL: graphql,
    MaxGraph: graph,
    MaxClusterNode: clusterNode,
    MaxTopologySizeByte: topology,
    MaxEdge: edge,
    MaxVertex: vertex,
    MaxCPUCore: cpu,
    MaxTotalCPUs: maxTotalCPUs,
    CRR: crr,
    DataHA: dataHA,
    MaxPhysicalMemorySizeByte: phyMem,
    MaxRSSMemorySizeByte: rssMem,
    Signature: signature,
    ContractID: contractID,
    NotifyInfo: notifyInfo
  } = detail;

  const downloadContract = async contractID => {
    if (contractID) {
      const res = await getContractFile(contractID);
      const filename = `contract-${audience}`;

      fileExport(res.data, 'application/pdf', filename);
    }
  };

  const renderExpiryDate = expiredAt => {
    const formattedString = moment(expiredAt).format('MMM DD, YYYY');

    const validPeriod = new Date(expiredAt).getTime() - new Date().getTime();

    const oneDayInMicroSecond = 60 * 60 * 24 * 1000;
    const quotient = validPeriod / oneDayInMicroSecond;

    const daysLeft = Math.floor(quotient);
    const dayUnit = daysLeft === 1 ? 'day' : 'days';
    const daysLeftString = `(${daysLeft} ${dayUnit} left)`;

    const renderResult = (
      <>
        <span>{formattedString}</span>
        &nbsp;
        {daysLeft > 365 ? (
          <span className={'inline-block ' + styles.daysLeft + ' ' + styles.moreThanOneYearLeft}>
            {daysLeftString}
          </span>
        ) : (
          <span className={'inline-block ' + styles.daysLeft}>{daysLeftString}</span>
        )}
      </>
    );

    return quotient > 7 ? (
      <Tooltip placement="top" title="Valid">
        <Badge color="green" text={renderResult} />
      </Tooltip>
    ) : quotient > 0 ? (
      <Tooltip placement="top" title="Expires within 7 days">
        <Badge color="orange" text={renderResult} />
      </Tooltip>
    ) : (
      <Tooltip placement="top" title="Expired">
        <Badge color="red" text={renderResult} />
      </Tooltip>
    );
  };

  return (
    <div className={styles.detailContainer}>
      <div className={styles.block}>
        <div className={styles.title}>
          <h3>Basic Info</h3>
          {contractID && (
            <Tooltip placement="top" title="Download contract file">
              <Button type={'primary'} onClick={() => downloadContract(contractID)}>
                Contract
              </Button>
            </Tooltip>
          )}
        </div>
        <Descriptions layout={'vertical'} column={5} bordered>
          {audience && <Descriptions.Item label="Audience">{audience}</Descriptions.Item>}

          {use && <Descriptions.Item label="Use">{use}</Descriptions.Item>}

          {type && <Descriptions.Item label="Type">{type}</Descriptions.Item>}

          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="Enable GraphStudio">{gst ? 'Yes' : 'No'}</Descriptions.Item>
          )}
          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="Enable Insights">
              {/* if there no field for insights/gsql shell/graphql, treat as if it is true */}
              {insights || insights === undefined ? 'Yes' : 'No'}
            </Descriptions.Item>
          )}
          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="Enable GSQL Shell">
              {gsqlshell || gsqlshell === undefined ? 'Yes' : 'No'}
            </Descriptions.Item>
          )}
          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="Enable GraphQL">
              {graphql || graphql === undefined ? 'Yes' : 'No'}
            </Descriptions.Item>
          )}
          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="CRR">
              {crr === undefined || crr === null ? 'N/A' : crr ? 'Yes' : 'No'}
            </Descriptions.Item>
          )}
          {type === LICENSE_TYPE_JWT && (
            <Descriptions.Item label="HA">
              {dataHA === undefined || dataHA === null ? 'N/A' : dataHA ? 'Yes' : 'No'}
            </Descriptions.Item>
          )}

          {endTime && (
            <Descriptions.Item label="Expired At">{renderExpiryDate(endTime)}</Descriptions.Item>
          )}
        </Descriptions>
      </div>

      {notifyInfo && notifyInfo.CustomerEmails && notifyInfo.CustomerEmails.length > 0 && (
        <div className={styles.block}>
          <Descriptions layout={'horizontal'} column={1} bordered>
            <Descriptions.Item label="Customer Emails">
              {notifyInfo.CustomerEmails.map((email, idx) => (
                <div key={idx}>{email}</div>
              ))}
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {notifyInfo && notifyInfo.CC && notifyInfo.CC.length > 0 && (
        <div className={styles.block}>
          <Descriptions layout={'horizontal'} column={1} bordered>
            <Descriptions.Item label="CC Emails">
              {notifyInfo.CC.map((email, idx) => (
                <div key={idx}>{email}</div>
              ))}
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {/* Use `number.toLocaleString()` to display number in a better way */}
      {graph && (
        <div className={styles.block}>
          <h3 className={styles.title}>Database Info</h3>
          <Descriptions layout={'vertical'} column={4} bordered>
            {graph && (
              <Descriptions.Item label="Number of Graph">
                {graph.toLocaleString()}
              </Descriptions.Item>
            )}

            {topology && (
              <Descriptions.Item label="Max Topology in GB">
                <div>
                  <div>{byteConversion(topology, 'GB').toLocaleString()}</div>
                  <div className={styles.topologyFieldNote}>
                    Topology size represents the total combined size of both the topology data and
                    the vector data.
                  </div>
                </div>
              </Descriptions.Item>
            )}

            {edge && (
              <Descriptions.Item label="Max Number of Edges">
                {edge.toLocaleString()}
              </Descriptions.Item>
            )}

            {vertex && (
              <Descriptions.Item label="Max Number of Vertices">
                {vertex.toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      )}

      {(type === 'jwt' || cpu != null || clusterNode || phyMem || rssMem) && (
        <div className={styles.block}>
          <h3 className={styles.title}>Machine Info</h3>
          <Descriptions layout={'vertical'} column={4} bordered>
            {clusterNode && (
              <Descriptions.Item label="Number of Cluster Nodes">
                {clusterNode.toLocaleString()}
              </Descriptions.Item>
            )}

            {(type === LICENSE_TYPE_JWT || (cpu !== null && cpu !== undefined)) && (
              <Descriptions.Item label="Max CPUs Per Node">
                {cpu !== null && cpu !== undefined ? cpu.toLocaleString() : 'N/A'}
              </Descriptions.Item>
            )}

            {type === LICENSE_TYPE_JWT && (
              <Descriptions.Item label="Max Total CPUs">
                {maxTotalCPUs != null ? maxTotalCPUs.toLocaleString() : 'N/A'}
              </Descriptions.Item>
            )}

            {phyMem && (
              <Descriptions.Item label="Max Physical Memory">
                {byteConversion(phyMem, 'GB').toLocaleString()} GB
              </Descriptions.Item>
            )}

            {rssMem && (
              <Descriptions.Item label="Max RSS (Resident Set Size)">
                {byteConversion(rssMem, 'GB').toLocaleString()} GB
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      )}

      {signature && signature.length !== 0 && (
        <div className={styles.block}>
          <h3 className={styles.title}>Host Signature</h3>
          <Collapse bordered={false}>
            <Panel header={'Signature'} key={1} style={customPanelStyle}>
              <div className={styles.codeBlock}>
                <SyntaxHighlighter language="json" style={tomorrow}>
                  {JSON.stringify(signature, undefined, 2)}
                </SyntaxHighlighter>
              </div>
            </Panel>
          </Collapse>
        </div>
      )}

      {notes && (
        <div className={styles.notesBlock}>
          <Descriptions layout={'vertical'} bordered>
            <Descriptions.Item label="Notes">
              <div className={styles.notes}>{notes}</div>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {isAdmin && status.toLowerCase() === 'pending' && (
        <div className={styles.notesBlock}>
          <Descriptions layout={'vertical'} bordered>
            <Descriptions.Item label="Operations for pending request">
              <NoteFormContainer id={id} closeModal={closeModal} />
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </div>
  );
};

export default LicenseDetail;
